import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globals } from '../globalfunctions.js';
import PageShell from '../components/ui/PageShell';
import FormField from '../components/ui/FormField';
import Button from '../components/ui/Button';
import { apiClient } from '../utils/apiClient';
import './MailSettings.css';

const EMPTY_FORM = {
  imap_host: '',
  imap_port: 993,
  imap_secure: true,
  imap_user: '',
  imap_password: '',
  inbox_folder: 'INBOX',
  processed_folder: 'Processed',
  junk_folder: 'Junk',
  polling_enabled: false,
  default_priority: 'medium',
  smtp_port: 465,
  smtp_secure: true,
  auto_close_enabled: true,
  auto_close_days: 4,
  satisfaction_survey_enabled: true,
  notify_customer_on_close: true,
  notify_staff_on_new_ticket: true,
  staff_notification_emails: '',
  logo_url: ''
};

function MailSettings() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pollResult, setPollResult] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [jobsEnabled, setJobsEnabled] = useState(true);
  const [configWarnings, setConfigWarnings] = useState([]);
  const [sendingTest, setSendingTest] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/api/mailSettings');
      setForm({ ...EMPTY_FORM, ...data, imap_password: '' });
      setLastChecked({ at: data.last_checked_at, status: data.last_check_status });
      setJobsEnabled(data.jobs_enabled !== false);
      setConfigWarnings(Array.isArray(data.config_warnings) ? data.config_warnings : []);
    } catch (error) {
      console.error('Error fetching mail settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Globals.LoginTime === null) {
      navigate('/login');
      return;
    }
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Upload/remove act immediately (the server stores the file and sets
  // logo_url itself) - they don't wait for "Save Settings".
  const uploadLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Logo must be under 2MB.');
      return;
    }
    setLogoBusy(true);
    setLogoError('');
    try {
      const data = await apiClient.upload('/api/mailSettings/logo', file);
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
    } catch (err) {
      setLogoError(err.message || 'Failed to upload logo');
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    setLogoError('');
    try {
      await apiClient.delete('/api/mailSettings/logo');
      setForm((f) => ({ ...f, logo_url: '' }));
    } catch (err) {
      setLogoError(err.message || 'Failed to remove logo');
    } finally {
      setLogoBusy(false);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/api/mailSettings', form);
      await fetchSettings();
    } catch (error) {
      console.error('Error saving mail settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await apiClient.post('/api/mailSettings/test-connection', form));
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    setEmailTestResult(null);
    try {
      setEmailTestResult(await apiClient.post('/api/mailSettings/test-email', testEmailTo.trim() ? { to: testEmailTo.trim() } : {}));
    } catch (error) {
      setEmailTestResult({ success: false, message: error.message });
    } finally {
      setSendingTest(false);
    }
  };

  const pollNow = async () => {
    setPolling(true);
    setPollResult(null);
    try {
      setPollResult(await apiClient.post('/api/mailSettings/poll-now'));
      fetchSettings();
    } catch (error) {
      setPollResult({ message: error.message });
    } finally {
      setPolling(false);
    }
  };

  if (loading) {
    return <PageShell><p>Loading mail settings…</p></PageShell>;
  }

  return (
    <PageShell>
      <div className="mail-settings-page">
        <h1>Support Inbox</h1>
        <p className="mail-settings-intro">
          Configure the mailbox support emails come in to. New emails become tickets automatically;
          replies to an existing ticket thread onto it. Processed emails are moved to the folder below
          so they aren't picked up twice. Blacklisted senders (see Whitelist / Blacklist in the Tickets
          menu) are moved straight to the junk folder and never become a ticket. Out-of-office replies
          and bounce-backs are detected automatically, filed into an "Auto-Replies" folder, and never
          replied to - this is what stops automated reply loops with a customer's autoresponder.
        </p>

        <form onSubmit={saveSettings} className="mail-settings-form">
          <div className="mail-settings-grid">
            <FormField id="imap_host" label="IMAP Host" required>
              <input id="imap_host" type="text" autoComplete="off" spellCheck={false} placeholder="mail.example.co.uk" value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} />
            </FormField>
            <FormField id="imap_port" label="Port">
              <input id="imap_port" type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value, 10) || 993 })} />
            </FormField>
            <FormField id="imap_user" label="Mailbox Username" required>
              <input id="imap_user" type="text" autoComplete="off" spellCheck={false} placeholder="support@yourdomain.co.uk" value={form.imap_user} onChange={(e) => setForm({ ...form, imap_user: e.target.value })} />
            </FormField>
            <FormField id="imap_password" label="Mailbox Password">
              <input id="imap_password" type="password" autoComplete="new-password" placeholder="Leave blank to keep current password" value={form.imap_password} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} />
            </FormField>
            <FormField id="inbox_folder" label="Inbox Folder">
              <input id="inbox_folder" type="text" value={form.inbox_folder} onChange={(e) => setForm({ ...form, inbox_folder: e.target.value })} />
            </FormField>
            <FormField id="processed_folder" label="Move Processed Emails To">
              <input id="processed_folder" type="text" value={form.processed_folder} onChange={(e) => setForm({ ...form, processed_folder: e.target.value })} />
            </FormField>
            <FormField id="junk_folder" label="Move Blacklisted Emails To">
              <input id="junk_folder" type="text" value={form.junk_folder} onChange={(e) => setForm({ ...form, junk_folder: e.target.value })} />
            </FormField>
            <FormField id="default_priority" label="Default Ticket Priority">
              <select id="default_priority" value={form.default_priority} onChange={(e) => setForm({ ...form, default_priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </FormField>
          </div>

          <div className="mail-settings-toggle">
            <label>
              <input type="checkbox" checked={form.polling_enabled} onChange={(e) => setForm({ ...form, polling_enabled: e.target.checked })} />
              Automatically check this inbox every 5 minutes
            </label>
          </div>

          <h2 className="mail-settings-section-title">Ticket Behaviour</h2>
          <p className="mail-settings-intro">
            Controls what happens once a ticket has been replied to and is waiting on the customer.
          </p>
          <div className="mail-settings-toggle">
            <label>
              <input type="checkbox" checked={form.auto_close_enabled} onChange={(e) => setForm({ ...form, auto_close_enabled: e.target.checked })} />
              Automatically close tickets that have been awaiting a reply from the customer for too long
            </label>
          </div>
          {form.auto_close_enabled && (
            <div className="mail-settings-grid">
              <FormField id="auto_close_days" label="Close after (days)">
                <input
                  id="auto_close_days"
                  type="number"
                  min="1"
                  value={form.auto_close_days}
                  onChange={(e) => setForm({ ...form, auto_close_days: parseInt(e.target.value, 10) || 1 })}
                />
              </FormField>
            </div>
          )}
          <div className="mail-settings-toggle">
            <label>
              <input type="checkbox" checked={form.notify_customer_on_close} onChange={(e) => setForm({ ...form, notify_customer_on_close: e.target.checked })} />
              Email the customer when their ticket closes
            </label>
          </div>
          {form.notify_customer_on_close && (
            <div className="mail-settings-toggle mail-settings-toggle-nested">
              <label>
                <input type="checkbox" checked={form.satisfaction_survey_enabled} onChange={(e) => setForm({ ...form, satisfaction_survey_enabled: e.target.checked })} />
                Also ask them to rate their support experience
              </label>
            </div>
          )}

          <h2 className="mail-settings-section-title">New Ticket Notifications</h2>
          <p className="mail-settings-intro">
            Get told the moment a new ticket comes in - useful for replying while you're out of the office.
          </p>
          <div className="mail-settings-toggle">
            <label>
              <input type="checkbox" checked={form.notify_staff_on_new_ticket} onChange={(e) => setForm({ ...form, notify_staff_on_new_ticket: e.target.checked })} />
              Email staff when a new ticket is created
            </label>
          </div>
          {form.notify_staff_on_new_ticket && (
            <div className="mail-settings-grid">
              <FormField id="staff_notification_emails" label="Notify these addresses">
                <input
                  id="staff_notification_emails"
                  type="text"
                  placeholder="Leave blank to notify all admins/sysadmins"
                  value={form.staff_notification_emails}
                  onChange={(e) => setForm({ ...form, staff_notification_emails: e.target.value })}
                />
              </FormField>
            </div>
          )}

          <h2 className="mail-settings-section-title">Branding</h2>
          <p className="mail-settings-intro">
            Shown in the top-left of every email (ticket replies, closures, new-ticket notifications).
            Upload a PNG, JPG, GIF or WebP up to 2MB; a white or transparent logo works best on the dark header.
            With no logo, the plain text "Vader Design" wordmark is used.
          </p>
          <div className="mail-settings-actions mail-settings-logo-actions">
            <input id="logo-file" type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={uploadLogo} hidden />
            <Button type="button" variant="secondary" disabled={logoBusy} onClick={() => document.getElementById('logo-file').click()}>
              {logoBusy ? 'Working…' : form.logo_url ? 'Replace logo' : 'Upload logo'}
            </Button>
            {form.logo_url && (
              <Button type="button" variant="secondary" disabled={logoBusy} onClick={removeLogo}>Remove logo</Button>
            )}
          </div>
          {logoError && <p className="mail-settings-error" role="alert">{logoError}</p>}
          <div className="mail-settings-grid">
            <FormField id="logo_url" label="Or use an image URL">
              <input
                id="logo_url"
                type="text"
                placeholder="https://example.com/logo.png"
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              />
            </FormField>
          </div>
          {form.logo_url && (
            <div className="mail-settings-logo-preview">
              <img src={form.logo_url} alt="Logo preview" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          )}

          <div className="mail-settings-actions">
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Settings'}</Button>
            <Button type="button" variant="secondary" onClick={testConnection} disabled={testing || !form.imap_host || !form.imap_user}>
              {testing ? 'Testing…' : 'Test Connection'}
            </Button>
            <input
              type="email"
              aria-label="Send test email to"
              placeholder="Test email to (blank = your own address)"
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
              style={{ minWidth: 260 }}
            />
            <Button type="button" variant="secondary" onClick={sendTestEmail} disabled={sendingTest}>
              {sendingTest ? 'Sending…' : 'Send Test Email'}
            </Button>
            <Button type="button" variant="secondary" onClick={pollNow} disabled={polling}>
              {polling ? 'Checking…' : 'Check Inbox Now'}
            </Button>
          </div>
        </form>

        {testResult && (
          <div className={`mail-settings-result ${testResult.success ? 'result-success' : 'result-error'}`}>
            {testResult.message}
          </div>
        )}

        {pollResult && (
          <div className="mail-settings-result result-info">
            {pollResult.message
              ? pollResult.message
              : `Processed ${pollResult.processed} email(s): ${pollResult.ticketsCreated} new ticket(s), ${pollResult.repliesThreaded} threaded reply/replies${pollResult.junked ? `, ${pollResult.junked} junked (blacklisted)` : ''}${pollResult.spamFiltered ? `, ${pollResult.spamFiltered} junked (flagged as spam by the mail server)` : ''}${pollResult.autoReplies ? `, ${pollResult.autoReplies} auto-reply/out-of-office (suppressed)` : ''}${pollResult.bounces ? `, ${pollResult.bounces} bounce(s) (suppressed)` : ''}.`}
            {pollResult.errors?.length > 0 && (
              <ul>{pollResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            )}
          </div>
        )}

        {configWarnings.map((warning) => (
          <div key={warning} className="mail-settings-result result-error">{warning}</div>
        ))}

        {emailTestResult && (
          <div className={`mail-settings-result ${emailTestResult.success ? 'result-success' : 'result-error'}`}>
            {emailTestResult.message}
          </div>
        )}

        {!jobsEnabled && (
          <div className="mail-settings-result result-error">
            Automatic inbox checking is switched off on this server (RUN_JOBS=false), so the
            5-minute poll is not running. Set RUN_JOBS=true on the API and restart it. "Check Inbox Now"
            still works manually.
          </div>
        )}

        {lastChecked?.at && (
          <p className="mail-settings-last-checked">
            Last checked: {new Date(lastChecked.at).toLocaleString('en-GB')} - {lastChecked.status}
          </p>
        )}
      </div>
    </PageShell>
  );
}

export default MailSettings;
